import { delay, throttle } from "lodash";
import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import React from "react";
import ReactDOM from "react-dom";

import timeoutRates from "../../constants/timeoutRates";
import { immutableRecords, PropTypeShapes } from "../../types";
import { hasNoSiblings } from "../../utils/domUtils";
import uniqueId from "../../utils/uniqueId";
import { ContainerContext, withContainerContext } from "../container/ContainerContext";
import { PortalChildContext } from "./PortalChildContext";
import { withPortalContext } from "./PortalContext";

const getContainerContextValue = memoizeOne((stateContainerRect, paddingLeft, paddingRight) => {
  return {
    containerRect: stateContainerRect,
    //#!if mobile === true
// paddingLeft: paddingLeft,
// paddingRight: paddingRight
    //#!endif
  };
});

class PortalChild extends React.PureComponent {
  constructor(props, context) {
    super(props, context);

    // Get the initial state
    let plainRect = this.getContainerState(props);
    // And convert it to a containerRect for sending to children
    plainRect.containerRect = immutableRecords.FlatRectRecord({
      left: plainRect.left,
      right: plainRect.right,
      width: plainRect.width,
      top: plainRect.top,
      bottom: plainRect.bottom,
      height: plainRect.height
    });

    this.state = plainRect;

    this._nodeId = "x" + uniqueId();
    this._domNode = null;
    this._callbacksToRegister = [];

    this.updateViewDimensions = this.updateViewDimensions.bind(this);
    this.throttleUpdateViewDimensions = throttle(
      this.updateViewDimensions,
      timeoutRates.PORTAL_UPDATE_DIMENSIONS_THROTTLE
    );
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.registerTransitionEndCallback = this.registerTransitionEndCallback.bind(this);
    this.unregisterTransitionEndCallback = this.unregisterTransitionEndCallback.bind(this);

    this._portalChildContext = {
      registerTransitionEndCallback: this.registerTransitionEndCallback,
      unregisterTransitionEndCallback: this.unregisterTransitionEndCallback
    };
    this.registerTransitionEndCallback(this.onTransitionEnd, false);

    this.onBlur = this.onBlur.bind(this);

    this._timerId = null;
  }

  componentDidMount() {
    if (this.props.portalContext && this.props.portalContext.onDidMount) {
      this.props.portalContext.onDidMount(this._nodeId);
    }
    this.registerWaitingCallbacks();
    this.updateViewDimensions();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.containerRect !== nextProps.containerRect) {
      this.updateViewDimensions(nextProps);
    }
  }

  componentDidUpdate() {
    this.throttleUpdateViewDimensions();
  }

  componentWillUnmount() {
    if (this.props.portalContext && this.props.portalContext.onWillUnmount) {
      this.props.portalContext.onWillUnmount(this._nodeId);
    }
    if (this._domNode) {
      this._domNode.removeEventListener("transitionend", this.onTransitionEnd);
    }
    if (this._timerId) {
      clearTimeout(this._timerId);
    }
    this.throttleUpdateViewDimensions.cancel();
  }

  onTransitionEnd(_e) {
    this.updateViewDimensions();
  }

  updateViewDimensions(useProps) {
    let newState = this.getContainerState(useProps || this.props);
    if (
      newState.left !== this.state.left ||
      newState.width !== this.state.width ||
      newState.top !== this.state.top ||
      newState.height !== this.state.height
    ) {
      newState.containerRect = immutableRecords.FlatRectRecord({
        left: newState.left,
        right: newState.right,
        width: newState.width,
        top: newState.top,
        bottom: newState.bottom,
        height: newState.height
      });
      this.setState(newState);
    }
  }

  getContainerState(useProps) {
    const testNode = this._domNode ? this._domNode : this.props.portalContext.portalNode;
    if (!testNode) {
      return {};
    }
    const domRect = testNode.getBoundingClientRect();

    const width = useProps.containerRect.width;
    const height = useProps.containerRect.height;

    let plainRect = {
      left: domRect.left,
      top: domRect.top,
      width,
      height,
      right: domRect.left + width,
      bottom: domRect.top + height
    };
    return plainRect;
  }

  registerWaitingCallbacks() {
    this._callbacksToRegister.forEach(obj => {
      this.registerTransitionEndCallback(obj.fn, obj.once);
    });
    this._callbacksToRegister = [];
  }

  registerTransitionEndCallback(fn, once) {
    if (!this._domNode) {
      this._callbacksToRegister.push({ fn, once });
      return;
    }
    // If it has no siblings, then it will be rendered straightaway without a transition
    // and so we should actually call that function immediately since there is no transition
    // to complete.
    if (this._domNode && hasNoSiblings(this._domNode)) {
      fn();
      if (once) {
        return;
      }
    }

    // Otherwise, it will initially be offscreen, so we want to send the message when it becomes
    // on-screen fully and it is safe to interact with it.
    this._domNode.addEventListener("transitionend", fn, { once });
  }

  unregisterTransitionEndCallback(fn) {
    // If PortalChild is removed first, its domNode reference will be set back to null
    // so we need to check for that.
    if (this._domNode) {
      this._domNode.removeEventListener("transitionend", fn);
    }
  }

  onBlur() {
    // Make sure the dimensions are updated after the virtual keyboard closing
    // See https://aeontimeline.atlassian.net/browse/ATR-2427
    this._timerId = delay(() => {
      this._timerId = null;
      this.updateViewDimensions();
    }, 100);
  }

  render() {
    let classNames = ["slidePortalPage"];

    let wrappedChildren = (
      <PortalChildContext.Provider value={this._portalChildContext}>
        <div
          onBlur={this.onBlur}
          id={this._nodeId}
          ref={node => (this._domNode = node)}
          className={classNames.join(" ")}
        >
          {this.props.children}
        </div>
      </PortalChildContext.Provider>
    );

    const containerContextValue = getContainerContextValue(
      this.state.containerRect,
      this.props.paddingLeft,
      this.props.paddingLeft
    );

    wrappedChildren = (
      <ContainerContext.Provider value={containerContextValue}>{wrappedChildren}</ContainerContext.Provider>
    );

    return ReactDOM.createPortal(wrappedChildren, this.props.portalContext.portalNode);
  }
}

PortalChild.propTypes = {
  children: PropTypeShapes.elementOrArray,
  // From portalContext
  portalContext: PropTypes.object,
  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
};

export default withPortalContext(withContainerContext(PortalChild));
